import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Box, useTheme } from "@mui/material";
import cx from 'classnames';
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Text from "../../../../theme/text/Text";
import { tokens } from "../../../../../theme";
import { reportFilterDataSource } from '../../chartFilterEnums';
import { useEffect, useState } from 'react';
import { DropdownBaseV2 } from '../../../../theme/dropdowns/Dropdown';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import { useReportHubFilters } from '../../ReportHubFilterContext';

export const ReportFilterDropdownFilter = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const filtersRegistry = useReportHubFilters();

    // resolve filter function
    const filter = filtersRegistry.filters[props.fKey];
    const resolvedFilter = typeof filter === "function" ? filter() : filter;

    const [filterData,setFilterData]= useState(null);
    
    const [runQuery, setRunQuery] = useState(false);
    const [filterDropdownOptionsSelected, setFilterDropdownOptionsSelected] = useState([]);
    
    const {data: queryData, isLoading, isFetching} = resolvedFilter.type === reportFilterDataSource.QUERY
        ? resolvedFilter.hook(resolvedFilter.hookArgs, { skip: !runQuery })
        : { data: null, isLoading: false, isFetching: false };

    // A filter can reshape what its source hands over before it is offered, like localizing a bucket
    // the backend only knows by its code.
    const applyMapData = (data) => resolvedFilter.mapData ? resolvedFilter.mapData(data) : data;

    const handleClick = () => {
        if (resolvedFilter.type === reportFilterDataSource.QUERY) {
            setRunQuery(true); 
        } else if (resolvedFilter.type === reportFilterDataSource.LOCAL) {
            const data = resolvedFilter.getData();
            setFilterData(applyMapData(data));
        }
    };

    useEffect(() => {
        if (resolvedFilter.type === reportFilterDataSource.QUERY) {
            if(queryData && !isLoading && !isFetching) {
                if(resolvedFilter.queryDataKey){
                    setFilterData(applyMapData(queryData[resolvedFilter.queryDataKey]));
                } else {
                    setFilterData(applyMapData(queryData));
                }
            }
        }
    },[queryData,resolvedFilter,isLoading,isFetching])

    // Options a filter always offers on top of whatever the select endpoint returns, like the 'manual'
    // bucket for orders without a channel.
    const options = [...(filterData || []), ...(resolvedFilter.dropdownOptions?.extraOptions ?? [])];

    // {console.log(props.filter)}
    return (
        <DropdownBaseV2
            options={options}
            selected={props.filter.value?.map(item => item.id)}
            displayName={resolvedFilter.dropdownOptions?.displayName ?? "name"}
            valueKey={resolvedFilter.dropdownOptions?.valueKey ?? "id"}
            searchOptions={{
                enabled: true,
                placeholder: t("search")
            }}
            allowSelectAll
            filterDropdownOptionsSelected={resolvedFilter.canBeInfluencedBy ? filterDropdownOptionsSelected : null}
            filterDropdownOptions={resolvedFilter.canBeInfluencedBy ?? null}
            filterDropdownOnSelect= {resolvedFilter.canBeInfluencedBy && Object.keys(resolvedFilter.canBeInfluencedBy) && ( (key,value) => {
                
            })}
            multiple
            selectOnClose={(selected) => {

                // console.log(selected);
                // compute selected items first
                const displayKey = resolvedFilter.dropdownOptions?.displayName ?? "name";
                const valueKey = resolvedFilter.dropdownOptions?.valueKey ?? "id";

                // Map selected indexes to objects with only displayName and valueKey
                // A saved value can be missing from the list, like a relation that has since gone inactive
                // or options that had not loaded yet. Keep the saved copy instead of crashing, which would
                // throw away the whole selection.
                const selectedItems = selected
                    .map((val) => options.find(fd => fd[valueKey] === val) ?? props.filter.value?.find(v => v[valueKey] === val))
                    .filter(Boolean)
                    .map((item) => ({
                        [displayKey]: item[displayKey],
                        [valueKey]: item[valueKey],
                    }));
                
                // update currentReport
                props.setCurrentReport((prev) => ({
                    ...prev,
                    filter: {
                        ...prev?.filter,
                        [props.fKey]: {
                            ...prev?.filter?.[props.fKey],
                            value: selectedItems,
                        }
                    }
                }));
            }}
        >
            {({anchorEl,onClick}) => (
                <Box
                    // key={"rp"+idx}
                    onClick={(e) => {
                        onClick(e);
                        handleClick();
                    }}
                    className={cx(undefined,{
                        "is-primary":props.filter.primary,
                        "wrapped-filter":props.wrapFilters,
                    })}
                    sx={{
                        px:0.5,
                        pl:0.5,
                        height:32,
                        display:"flex",
                        justifyContent:"flex-start",
                        alignItems:'center',
                        background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                        borderRadius:4,
                        cursor:"pointer",
                        "&.is-primary" : {
                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                            border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                        },
                        "&:hover .rp-value-container" : {
                            pr:4,
                        },
                        "&:hover .rp-value-container .rp-filter-icon" : {
                            zIndex:1,
                            opacity:1,
                                transition:"125ms",
                        },
                        "&.wrapped-filter" :{
                            width:1,
                        }
                    }}
                >

                    
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();

                            props.setCurrentReport((prev) => {
                                if (!prev?.filter) return prev;

                                const newFilters = {};

                                for (const key in prev.filter) {
                                    // toggle primary for the clicked filter, unset for all others
                                    newFilters[key] = {
                                        ...prev.filter[key],
                                        primary: key === props.fKey ? !prev.filter[key].primary : false,
                                    };
                                }

                                return {
                                    ...prev,
                                    filter: newFilters,
                                };
                            });
                        }}
                        className={cx("rp-filter-icon", {
                            "is-primary": props.filter.primary,
                        })}
                        sx={{
                            mr:1,
                            display:'flex',
                            justifyContent:'center',
                            alignItems:'center',
                            flexShrink:0,
                            transition:"125ms",
                            height:24,
                            width:24,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            color:colors.txt['primary'],
                            borderRadius:4,
                            "&.is-primary": {
                                background:theme.palette.mode === "dark" ?colors.blue[400] : colors.primary[400],
                                color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                            }
                        }}
                    >
                        {props.filter.primary ?
                        <StarIcon fontSize='small'/> : <StarBorderIcon fontSize='small'/>
                        }
                    </Box>

                    <Text noBreak sx={{mr:2}}>
                        {t(props.fKey)}
                    </Text>
                    <Box
                        className={cx("rp-value-container",{
                            "is-primary":props.filter.primary,
                            "auto-align-right":props.wrapFilters,
                        })}
                        sx={{
                            position:"relative",
                            display:"flex",
                            alignItems:'center',
                            borderRadius:4,
                            color:colors.txt["primary"],
                            transition:"250ms",

                            background:colors.red[400],
                            color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                            "&.auto-align-right" : {
                                ml:"auto",
                            }
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius:3,
                                px:1.5,
                                // One flex line: in the builder's narrow filter column the count and
                                // the word would otherwise wrap onto two lines and throw the pill out
                                // of line with its label.
                                display:"flex",
                                alignItems:"center",
                                background:theme.palette.mode === "dark" ? props.filter.primary ? colors.grey[200] : colors.grey[400] : colors.grey[0],
                            }}
                        >
                            {props.filter.value.length > 0 ?
                                <>
                                    <Text noBreak bold>{props.filter.value.length}</Text>
                                    <Text noBreak light sx={{ml:0.5,}}>
                                        {t("selected")}
                                    </Text>
                                </>
                            :
                                <Text noBreak>{t("no_selection")}</Text>
                            }
                        </Box>
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();

                                props.setCurrentReport((prev) => {
                                    if (!prev?.filter) return prev;

                                    const { [props.fKey]: _, ...remainingFilters } = prev.filter;

                                    return {
                                    ...prev,
                                    filter: remainingFilters,
                                    };
                                });
                            }}
                            className={cx("rp-filter-icon", {
                                "is-primary": props.filter.primary,
                                'showDelete':props.showDelete,
                            })}
                            sx={{
                                position:'absolute',
                                display:'flex',
                                alignItems:'center',
                                px:1,
                                pl:0.5,

                                right:"0px",
                                opacity:0,
                                transition:"125ms",
                                // transitionDelay:"125ms",

                                zIndex:-1,
                                "&.showDelete":{
                                    position:"static",
                                    opacity:1,
                                    zIndex:2,
                                }
                                // "&.is-primary":{
                                //     right:"0px",
                                //     opacity:1,
                                //     zIndex:1,
                                // }
                            }}
                        >
                            <CloseIcon fontSize="small"/>
                        </Box>
                    </Box>
                </Box>
            )}
        </DropdownBaseV2>
    )
}